import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import { pdf } from "@react-pdf/renderer";
import { v4 as uuidv4 } from "uuid";
import InvoicePDF from "./PDF/InvoicePDF";
import "./invoice.css";
import {
  Save,
  RotateCcw,
  FileDown,
  PlusCircle,
  Trash2,
  Building2,
  User2,
  CalendarDays,
  ChevronLeft,
  Mail,
  Phone,
  MapPin,
  BadgeCheck,
} from "lucide-react";

function generateInvoiceNumber() {
  return `INV-${Math.floor(Math.random() * 1000000)}`;
}

function round2(value) {
  return Number(Number(value || 0).toFixed(2));
}

const CreateInvoice = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { companyId, company } = useCompany();

  const [userId, setUserId] = useState(null);
  const [companyInfo, setCompanyInfo] = useState({});
  const [customers, setCustomers] = useState([]);
  const [currentInvoiceId, setCurrentInvoiceId] = useState(id || null);
  const [journalPosted, setJournalPosted] = useState(false);
  const [journalBusy, setJournalBusy] = useState(false);
  const [showTaxAdvanced, setShowTaxAdvanced] = useState(false);

  const [invoiceData, setInvoiceData] = useState({
    customer: "",
    customer_id: null,
    address: "",
    date: new Date().toISOString().split("T")[0],
    expiryDate: "",
    invoiceNumber: generateInvoiceNumber(),
    notes: "",
    vat_rate: 0.075,
    wht_rate: 0,
    total: 0,
    status: "open",
    items: [],
  });

  const [insertedItems, setInsertedItems] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newItem, setNewItem] = useState({
    description: "",
    quantity: 0,
    unitPrice: 0,
    lineTotal: 0,
  });

  const taxSummary = useMemo(() => {
    const subtotal = round2(invoiceData.total || 0);
    const vatRate = Number(invoiceData.vat_rate || 0);
    const whtRate = Number(invoiceData.wht_rate || 0);

    const vatAmount = round2(subtotal * vatRate);
    const grossTotal = round2(subtotal + vatAmount);

    // WHT is usually deducted on the taxable/service value, not VAT-inclusive total
    const whtAmount = round2(subtotal * whtRate);
    const netReceivable = round2(grossTotal - whtAmount);

    return {
      subtotal,
      vatRate,
      vatAmount,
      grossTotal,
      whtRate,
      whtAmount,
      netReceivable,
    };
  }, [invoiceData.total, invoiceData.vat_rate, invoiceData.wht_rate]);

  const checkJournalPosted = useCallback(
    async (invoiceIdToCheck) => {
      if (!companyId || !invoiceIdToCheck) {
        setJournalPosted(false);
        return false;
      }

      const { data, error } = await supabase
        .from("journal_entries")
        .select("id")
        .eq("company_id", companyId)
        .eq("source_type", "invoice")
        .eq("source_ref", String(invoiceIdToCheck))
        .limit(1);

      if (error) {
        console.error("Failed to check posted journal:", error);
        setJournalPosted(false);
        return false;
      }

      const exists = !!(data && data.length);
      setJournalPosted(exists);
      return exists;
    },
    [companyId],
  );

  const findAccountByNameOrType = useCallback(async () => {
    if (!companyId) throw new Error("Company not loaded");

    const { data, error } = await supabase
      .from("chart_of_accounts")
      .select("id, name, type, category")
      .eq("company_id", companyId);

    if (error) {
      throw new Error(error.message || "Failed to load chart_of_accounts");
    }

    const accounts = data || [];

    const exactName = (names) =>
      accounts.find((a) =>
        names.some(
          (n) =>
            String(a.name || "")
              .trim()
              .toLowerCase() === n.toLowerCase(),
        ),
      );

    const typeMatch = (typeName) =>
      accounts.find(
        (a) =>
          String(a.type || "")
            .trim()
            .toLowerCase() === String(typeName).trim().toLowerCase(),
      );

    const receivableAccount =
      exactName([
        "Accounts Receivable",
        "Account Receivables",
        "Trade Receivables",
      ]) ||
      accounts.find((a) =>
        ["accounts receivable", "trade receivables"].includes(
          String(a.name || "")
            .trim()
            .toLowerCase(),
        ),
      );

    const revenueAccount =
      exactName(["Revenue", "Sales Revenue", "Service Revenue"]) ||
      typeMatch("Revenue");

    const vatPayablesAccount = exactName(["VAT Payables", "VAT Payable"]);
    const whtReceivablesAccount = exactName([
      "WHT Receivables",
      "WHT Receivable",
    ]);

    if (!receivableAccount) {
      throw new Error(
        "Accounts Receivable account not found in chart_of_accounts",
      );
    }

    if (!revenueAccount) {
      throw new Error("Revenue account not found in chart_of_accounts");
    }

    if (Number(taxSummary.vatAmount || 0) > 0 && !vatPayablesAccount) {
      throw new Error("VAT Payables account not found in chart_of_accounts");
    }

    if (Number(taxSummary.whtAmount || 0) > 0 && !whtReceivablesAccount) {
      throw new Error("WHT Receivables account not found in chart_of_accounts");
    }

    return {
      receivableAccount,
      revenueAccount,
      vatPayablesAccount,
      whtReceivablesAccount,
    };
  }, [companyId, taxSummary.vatAmount, taxSummary.whtAmount]);

  // Load user, company info, customers, and existing invoice (if editing)
  useEffect(() => {
    const fetchData = async () => {
      const {
        data: { user },
        error: userErr,
      } = await supabase.auth.getUser();

      if (userErr || !user) {
        console.error("Auth error:", userErr);
        return;
      }

      setUserId(user.id);
      if (!companyId) return;

      let ownerId = company?.owner_id;
      if (!ownerId) {
        const { data: compRow } = await supabase
          .from("companies")
          .select("owner_id")
          .eq("id", companyId)
          .single();
        ownerId = compRow?.owner_id;
      }

      if (ownerId) {
        const { data: ci } = await supabase
          .from("company_info")
          .select(
            "company_name,email,address,phone_number,TIN,logo_url,brand_colors,bank_name,account_name,account_number,payment_instructions",
          )
          .eq("company_id", companyId)
          .single();

        if (ci) setCompanyInfo(ci);
      }

      const { data: custs } = await supabase
        .from("customers")
        .select("id,company_name,address")
        .eq("company_id", companyId);

      const custList = custs || [];
      setCustomers(custList);

      if (isEdit) {
        const { data: inv, error: invErr } = await supabase
          .from("invoices")
          .select("*, items")
          .eq("id", id)
          .eq("company_id", companyId)
          .maybeSingle();

        if (invErr) {
          console.error("Error loading invoice:", invErr);
          return;
        }

        if (inv) {
          const selected = custList.find((c) => c.id === inv.customer_id);

          setInvoiceData({
            customer: inv.customer || selected?.company_name || "",
            customer_id: inv.customer_id || null,
            address: inv.address || selected?.address || "",
            date: inv.date || new Date().toISOString().split("T")[0],
            expiryDate: inv.expiryDate || "",
            invoiceNumber: inv.invoiceNumber || generateInvoiceNumber(),
            notes: inv.notes || "",
            vat_rate: Number(inv.vat_rate ?? 0.075),
            wht_rate: Number(inv.wht_rate ?? inv.tax_json?.wht_rate ?? 0),
            total: Number(inv.total || 0),
            status: inv.status || "open",
            items: inv.items || [],
          });

          setInsertedItems(inv.items || []);
          setCurrentInvoiceId(inv.id);
          await checkJournalPosted(inv.id);
        }
      } else {
        setCurrentInvoiceId(null);
        setJournalPosted(false);
      }
    };

    fetchData();
  }, [companyId, company, id, isEdit, checkJournalPosted]);

  // set dynamic brand color (existing behavior)
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--primary-color",
      companyInfo.brand_colors || "#008080",
    );
  }, [companyInfo.brand_colors]);

  // Recalc totals when items change (existing behavior)
  useEffect(() => {
    const subtotal = insertedItems.reduce(
      (sum, it) =>
        sum + (Number(it.quantity || 0) * Number(it.unitPrice || 0) || 0),
      0,
    );

    setInvoiceData((prev) => ({
      ...prev,
      total: round2(subtotal),
      items: insertedItems,
    }));
  }, [insertedItems]);

  const handleCustomerChange = (e) => {
    const custId = e.target.value || "";
    const cust = customers.find((c) => c.id === custId);

    if (cust) {
      setInvoiceData((prev) => ({
        ...prev,
        customer_id: cust.id,
        customer: cust.company_name,
        address: cust.address,
      }));
    } else {
      setInvoiceData((prev) => ({
        ...prev,
        customer_id: null,
        customer: "",
        address: "",
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const numericFields = ["vat_rate", "wht_rate"];
    setInvoiceData((prev) => ({
      ...prev,
      [name]: numericFields.includes(name) ? Number(value || 0) : value,
    }));
  };

  const handleModalInputChange = (e) => {
    const { name, value } = e.target;
    setNewItem((prev) => ({ ...prev, [name]: value }));
  };

  const insertItem = () => {
    const item = {
      ...newItem,
      quantity: Number(newItem.quantity) || 0,
      unitPrice: Number(newItem.unitPrice) || 0,
      lineTotal:
        (Number(newItem.quantity) || 0) * (Number(newItem.unitPrice) || 0),
    };

    setInsertedItems((prev) => [...prev, item]);
    setNewItem({ description: "", quantity: 0, unitPrice: 0, lineTotal: 0 });
    setIsModalOpen(false);
  };

  const updateItem = (index, field, value) => {
    setInsertedItems((prev) => {
      const items = [...prev];
      items[index][field] = value;
      items[index].lineTotal =
        (Number(items[index].quantity) || 0) *
        (Number(items[index].unitPrice) || 0);
      return items;
    });
  };

  const removeItem = (index) => {
    setInsertedItems((prev) => prev.filter((_, i) => i !== index));
  };

  const saveInvoice = useCallback(
    async ({ silent = false, stayOnPage = false } = {}) => {
      if (!userId || !companyId) return null;

      const targetInvoiceId = isEdit ? id : currentInvoiceId;

      const payload = {
        ...invoiceData,
        user_id: userId,
        company_id: companyId,
        customer_id: invoiceData.customer_id || null,
        vat_amount: taxSummary.vatAmount,
        wht_amount: taxSummary.whtAmount,
        gross_total: taxSummary.grossTotal,
        net_receivable: taxSummary.netReceivable,
        tax_json: {
          subtotal: taxSummary.subtotal,
          vat_rate: taxSummary.vatRate,
          vat_amount: taxSummary.vatAmount,
          wht_rate: taxSummary.whtRate,
          wht_amount: taxSummary.whtAmount,
          gross_total: taxSummary.grossTotal,
          net_receivable: taxSummary.netReceivable,
        },
      };

      try {
        let savedRow = null;

        if (targetInvoiceId) {
          const { data, error } = await supabase
            .from("invoices")
            .update(payload)
            .eq("id", targetInvoiceId)
            .eq("company_id", companyId)
            .select()
            .single();

          if (error) throw error;
          savedRow = data;
          if (!silent) {
            alert("Invoice updated successfully!");
          }
        } else {
          const { data, error } = await supabase
            .from("invoices")
            .insert([payload])
            .select()
            .single();

          if (error) throw error;
          savedRow = data;
          if (!silent) {
            alert("Invoice saved successfully!");
          }
        }

        const savedId = savedRow?.id || targetInvoiceId || null;
        setCurrentInvoiceId(savedId);

        if (!stayOnPage) {
          navigate("/total_invoice");
        }

        return savedId;
      } catch (error) {
        console.error(error);
        if (!silent) {
          alert(error.message || "Failed to save invoice");
        }
        return null;
      }
    },
    [
      userId,
      companyId,
      invoiceData,
      id,
      isEdit,
      currentInvoiceId,
      navigate,
      taxSummary,
    ],
  );

  const postInvoiceJournal = async () => {
    if (!companyId || !userId) {
      alert("Company or user not loaded.");
      return;
    }

    setJournalBusy(true);

    try {
      let invoiceIdToUse = currentInvoiceId;

      if (!invoiceIdToUse) {
        invoiceIdToUse = await saveInvoice({ silent: true, stayOnPage: true });
        if (!invoiceIdToUse) {
          throw new Error("Invoice must be saved before posting journal");
        }
      }

      const alreadyPosted = await checkJournalPosted(invoiceIdToUse);
      if (alreadyPosted) {
        alert("Journal already posted for this invoice.");
        return;
      }

      const {
        receivableAccount,
        revenueAccount,
        vatPayablesAccount,
        whtReceivablesAccount,
      } = await findAccountByNameOrType();

      const journalId = uuidv4();
      const subtotal = Number(taxSummary.subtotal || 0);
      const vatAmount = Number(taxSummary.vatAmount || 0);
      const grossTotal = Number(taxSummary.grossTotal || 0);
      const whtAmount = Number(taxSummary.whtAmount || 0);
      const netReceivable = Number(taxSummary.netReceivable || 0);

      const descriptionBase = `Invoice ${invoiceData.invoiceNumber} - ${
        invoiceData.customer || "Customer"
      }`;

      const entries = [];

      // DR Accounts Receivable
      entries.push({
        journal_id: journalId,
        user_id: userId,
        company_id: companyId,
        account_id: receivableAccount.id,
        date: invoiceData.date,
        description:
          whtAmount > 0
            ? `${descriptionBase} (Net receivable)`
            : `${descriptionBase} (Gross receivable)`,
        money_in: whtAmount > 0 ? netReceivable : grossTotal,
        money_out: 0,
        source_type: "invoice",
        source_ref: String(invoiceIdToUse),
        created_by: userId,
        posting_status: "posted",
        counterparty_type: "customer",
        counterparty_id: invoiceData.customer_id || null,
        due_date: invoiceData.expiryDate || null,
        currency_code: "NGN",
        fx_rate: 1,

        tax_type: null,
        tax_base_amount: 0,
        tax_rate: 0,
        tax_amount: 0,
        taxable_flag: false,
        recoverable_flag: false,
        exempt_flag: false,
        zero_rated_flag: false,
        withholding_flag: false,
        deductible_flag: true,
      });

      // Optional DR WHT Receivables
      if (whtAmount > 0) {
        entries.push({
          journal_id: journalId,
          user_id: userId,
          company_id: companyId,
          account_id: whtReceivablesAccount.id,
          date: invoiceData.date,
          description: `${descriptionBase} (WHT receivable)`,
          money_in: whtAmount,
          money_out: 0,
          source_type: "invoice",
          source_ref: String(invoiceIdToUse),
          created_by: userId,
          posting_status: "posted",
          counterparty_type: "customer",
          counterparty_id: invoiceData.customer_id || null,
          due_date: invoiceData.expiryDate || null,
          currency_code: "NGN",
          fx_rate: 1,
          tax_type: "WHT",
          tax_base_amount: subtotal,
          tax_rate: Number(invoiceData.wht_rate || 0),
          tax_amount: whtAmount,
          taxable_flag: true,
          recoverable_flag: true,
          exempt_flag: false,
          zero_rated_flag: false,
          withholding_flag: true,
          deductible_flag: true,
        });
      }

      // CR Revenue
      entries.push({
        journal_id: journalId,
        user_id: userId,
        company_id: companyId,
        account_id: revenueAccount.id,
        date: invoiceData.date,
        description: `${descriptionBase} (Revenue)`,
        money_in: 0,
        money_out: subtotal,
        source_type: "invoice",
        source_ref: String(invoiceIdToUse),
        created_by: userId,
        posting_status: "posted",
        counterparty_type: "customer",
        counterparty_id: invoiceData.customer_id || null,
        due_date: invoiceData.expiryDate || null,
        currency_code: "NGN",
        fx_rate: 1,

        tax_type: null,
        tax_base_amount: 0,
        tax_rate: 0,
        tax_amount: 0,
        taxable_flag: false,
        recoverable_flag: false,
        exempt_flag: false,
        zero_rated_flag: false,
        withholding_flag: false,
        deductible_flag: true,
      });

      // CR VAT Payables
      if (vatAmount > 0) {
        entries.push({
          journal_id: journalId,
          user_id: userId,
          company_id: companyId,
          account_id: vatPayablesAccount.id,
          date: invoiceData.date,
          description: `${descriptionBase} (VAT payable)`,
          money_in: 0,
          money_out: vatAmount,
          source_type: "invoice",
          source_ref: String(invoiceIdToUse),
          created_by: userId,
          posting_status: "posted",
          counterparty_type: "customer",
          counterparty_id: invoiceData.customer_id || null,
          due_date: invoiceData.expiryDate || null,
          currency_code: "NGN",
          fx_rate: 1,
          tax_type: "VAT",
          tax_base_amount: subtotal,
          tax_rate: Number(invoiceData.vat_rate || 0),
          tax_amount: vatAmount,
          taxable_flag: true,
          recoverable_flag: false,
          exempt_flag: false,
          zero_rated_flag: false,
          withholding_flag: false,
          deductible_flag: true,
        });
      }

      const totalDr = entries.reduce(
        (sum, row) => sum + Number(row.money_in || 0),
        0,
      );
      const totalCr = entries.reduce(
        (sum, row) => sum + Number(row.money_out || 0),
        0,
      );

      if (round2(totalDr) !== round2(totalCr)) {
        throw new Error(
          `Invoice journal is not balanced. Debit: ${totalDr}, Credit: ${totalCr}`,
        );
      }

      const { error } = await supabase.from("journal_entries").insert(entries);

      if (error) {
        throw new Error(error.message || "Failed to post invoice journal");
      }

      setCurrentInvoiceId(invoiceIdToUse);
      setJournalPosted(true);
      alert("Invoice journal posted successfully!");
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to post invoice journal");
    } finally {
      setJournalBusy(false);
    }
  };

  const deleteInvoiceJournal = async () => {
    if (!companyId || !currentInvoiceId) {
      alert("Invoice not loaded.");
      return;
    }

    setJournalBusy(true);

    try {
      const { error } = await supabase
        .from("journal_entries")
        .delete()
        .eq("company_id", companyId)
        .eq("source_type", "invoice")
        .eq("source_ref", String(currentInvoiceId));

      if (error) {
        throw new Error(error.message || "Failed to delete invoice journal");
      }

      setJournalPosted(false);
      alert("Invoice journal deleted successfully!");
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to delete invoice journal");
    } finally {
      setJournalBusy(false);
    }
  };

  const handleJournalToggle = async () => {
    if (journalPosted) {
      await deleteInvoiceJournal();
    } else {
      await postInvoiceJournal();
    }
  };

  const printInvoice = async () => {
    try {
      const accent =
        getComputedStyle(document.documentElement)
          .getPropertyValue("--primary-color")
          .trim() || "#008080";

      const normalizedStatus = String(invoiceData?.status || "open")
        .toLowerCase()
        .trim();

      const pdfInvoiceData = {
        ...invoiceData,
        vat_amount: taxSummary.vatAmount,
        wht_amount: taxSummary.whtAmount,
        gross_total: taxSummary.grossTotal,
        net_receivable: taxSummary.netReceivable,
        status:
          normalizedStatus === "paid" ||
          normalizedStatus === "settled" ||
          normalizedStatus === "complete" ||
          normalizedStatus === "completed"
            ? "paid"
            : normalizedStatus,
      };

      const doc = (
        <InvoicePDF
          companyInfo={companyInfo}
          invoiceData={pdfInvoiceData}
          items={insertedItems}
          accent={accent}
        />
      );

      const blob = await pdf(doc).toBlob();

      const safeNo = String(pdfInvoiceData.invoiceNumber || "invoice")
        .replace(/[^\w-]+/g, "_")
        .slice(0, 80);

      const fileName = `${safeNo}_${
        pdfInvoiceData.status === "paid" ? "PAID" : "OPEN"
      }.pdf`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Could not generate PDF. Please try again.");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      e.target.form
        ?.querySelectorAll("input, textarea, select")
        [Array.from(e.target.form.elements).indexOf(e.target) + 1]?.focus();
    }
  };

  return (
    <div className="invoice-shell">
      {/* LEFT: editor */}
      <section className="invoice-input panel">
        <header className="inv-head">
          <div className="inv-title">
            <h2>{isEdit ? "Edit Invoice" : "Create Invoice"}</h2>
            <p className="muted">
              <Building2 size={14} /> {companyInfo.company_name || "—"}{" "}
              &nbsp;•&nbsp;
              <User2 size={14} /> {invoiceData.customer || "—"} &nbsp;•&nbsp;
              <CalendarDays size={14} /> {invoiceData.date || "—"}
            </p>
            <p className="muted" style={{ marginTop: "6px" }}>
              Journal Status:{" "}
              <strong>{journalPosted ? "Posted" : "Not Posted"}</strong>
            </p>
          </div>

          <div className="inv-actions">
            <button className="btn-ghost" onClick={() => navigate(-1)}>
              <ChevronLeft size={16} /> Back
            </button>

            <button className="btn-primary" onClick={() => saveInvoice()}>
              <Save size={16} />{" "}
              {isEdit || currentInvoiceId ? "Update" : "Save"}
            </button>

            <button
              className={journalPosted ? "btn-ghost" : "btn-primary"}
              onClick={handleJournalToggle}
              disabled={journalBusy}
              title={
                journalPosted
                  ? "Delete invoice journal only"
                  : "Post invoice journal"
              }
            >
              {journalBusy
                ? "Working..."
                : journalPosted
                  ? "Delete Journal"
                  : "Post Journal"}
            </button>

            <button
              className="icon-btn"
              onClick={() => window.location.reload()}
              title="Reset"
            >
              <RotateCcw size={16} />
            </button>

            <button
              className="icon-btn"
              onClick={printInvoice}
              title="Download PDF"
            >
              <FileDown size={16} />
            </button>
          </div>
        </header>

        <div className="inv-card">
          <div className="grid-3">
            <label className="stack">
              Customer
              {customers.length ? (
                <select
                  value={invoiceData.customer_id || ""}
                  onChange={handleCustomerChange}
                >
                  <option value="">Select</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.company_name}
                    </option>
                  ))}
                </select>
              ) : (
                <button
                  className="btn-ghost"
                  onClick={() => navigate("/customers")}
                >
                  <PlusCircle size={16} /> Add Customer
                </button>
              )}
            </label>

            <label className="stack">
              Address
              <input
                name="address"
                value={invoiceData.address}
                onChange={handleInputChange}
                placeholder="Customer billing address"
              />
            </label>

            <div className="grid-2">
              <label className="stack">
                Date
                <input
                  type="date"
                  name="date"
                  value={invoiceData.date}
                  onChange={handleInputChange}
                />
              </label>
              <label className="stack">
                Due
                <input
                  type="date"
                  name="expiryDate"
                  value={invoiceData.expiryDate}
                  onChange={handleInputChange}
                />
              </label>
            </div>

            <label className="stack">
              Invoice #
              <input
                name="invoiceNumber"
                value={invoiceData.invoiceNumber}
                readOnly
              />
            </label>

            <label className="stack">
              Status
              <select
                name="status"
                value={invoiceData.status}
                onChange={handleInputChange}
              >
                <option value="open">Open</option>
                <option value="paid">Paid</option>
              </select>
            </label>
          </div>
        </div>

        <div className="inv-card">
          <div className="card-head">
            <h3>Tax Settings</h3>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setShowTaxAdvanced((s) => !s)}
            >
              {showTaxAdvanced ? "Hide Advanced" : "Show Advanced"}
            </button>
          </div>

          <div className="grid-3">
            <label className="stack">
              VAT Rate %
              <input
                type="number"
                step="0.01"
                min="0"
                name="vat_rate"
                value={Number(invoiceData.vat_rate || 0) * 100}
                onChange={(e) =>
                  setInvoiceData((prev) => ({
                    ...prev,
                    vat_rate: Number(e.target.value || 0) / 100,
                  }))
                }
              />
            </label>

            <label className="stack">
              WHT Rate %
              <input
                type="number"
                step="0.01"
                min="0"
                name="wht_rate"
                value={Number(invoiceData.wht_rate || 0) * 100}
                onChange={(e) =>
                  setInvoiceData((prev) => ({
                    ...prev,
                    wht_rate: Number(e.target.value || 0) / 100,
                  }))
                }
              />
            </label>

            <label className="stack">
              Net Receivable
              <input
                value={`₦${taxSummary.netReceivable.toLocaleString()}`}
                readOnly
              />
            </label>
          </div>

          {showTaxAdvanced && (
            <div className="grid-4" style={{ marginTop: "12px" }}>
              <label className="stack">
                Subtotal
                <input
                  value={`₦${taxSummary.subtotal.toLocaleString()}`}
                  readOnly
                />
              </label>

              <label className="stack">
                VAT Amount
                <input
                  value={`₦${taxSummary.vatAmount.toLocaleString()}`}
                  readOnly
                />
              </label>

              <label className="stack">
                Gross Total
                <input
                  value={`₦${taxSummary.grossTotal.toLocaleString()}`}
                  readOnly
                />
              </label>

              <label className="stack">
                WHT Amount
                <input
                  value={`₦${taxSummary.whtAmount.toLocaleString()}`}
                  readOnly
                />
              </label>
            </div>
          )}
        </div>

        <div className="inv-card">
          <div className="card-head">
            <h3>Items</h3>
            <button onClick={() => setIsModalOpen(true)} className="btn-ghost">
              <PlusCircle size={16} /> Insert Item
            </button>
          </div>

          <div className="table-wrap">
            <table className="invoice-items-edit">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="num">Qty</th>
                  <th className="num">Rate</th>
                  <th className="num">Amount</th>
                  <th className="center">Action</th>
                </tr>
              </thead>
              <tbody>
                {insertedItems.map((item, idx) => (
                  <tr key={idx}>
                    <td>
                      <input
                        value={item.description}
                        onChange={(e) =>
                          updateItem(idx, "description", e.target.value)
                        }
                      />
                    </td>
                    <td className="num">
                      <input
                        type="number"
                        value={item.quantity}
                        id="edit-input"
                        onChange={(e) =>
                          updateItem(
                            idx,
                            "quantity",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                      />
                    </td>
                    <td className="num">
                      <input
                        type="number"
                        value={item.unitPrice}
                        id="edit-input"
                        onChange={(e) =>
                          updateItem(
                            idx,
                            "unitPrice",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                      />
                    </td>
                    <td className="num">
                      ₦{(item.lineTotal || 0).toLocaleString()}
                    </td>
                    <td className="center">
                      <button
                        type="button"
                        className="icon-btn danger"
                        onClick={() => removeItem(idx)}
                        title="Remove"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}

                {!insertedItems.length && (
                  <tr>
                    <td colSpan="5">
                      <div className="empty-state">
                        No items yet. Click <em>Insert Item</em> to add your
                        first line.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="inv-grid">
          <div className="inv-card">
            <h3>Notes</h3>
            <textarea
              name="notes"
              placeholder="Payment terms, delivery notes, thank you message…"
              value={invoiceData.notes}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
            />
          </div>

          <div className="inv-card">
            <h3>Summary</h3>
            <ul className="kv">
              <li>
                <span>Sub-Total</span>
                <strong>₦{taxSummary.subtotal.toLocaleString()}</strong>
              </li>
              <li>
                <span>VAT ({(taxSummary.vatRate || 0) * 100}%)</span>
                <strong>₦{taxSummary.vatAmount.toLocaleString()}</strong>
              </li>
              <li>
                <span>Gross Total</span>
                <strong>₦{taxSummary.grossTotal.toLocaleString()}</strong>
              </li>
              <li>
                <span>WHT ({(taxSummary.whtRate || 0) * 100}%)</span>
                <strong>₦{taxSummary.whtAmount.toLocaleString()}</strong>
              </li>
              <li className="grand">
                <span>Net Receivable</span>
                <strong>₦{taxSummary.netReceivable.toLocaleString()}</strong>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* RIGHT: preview (IDs preserved) */}
      <aside id="invoicePreview" className="invoice-preview panel">
        {companyInfo.logo_url && (
          <img src={companyInfo.logo_url} alt="Logo" className="logo" />
        )}

        <div id="comp-div">
          <p id="comp-phone">
            <Phone size={8} /> <span>{companyInfo.phone_number}</span>
          </p>
          <p id="comp-email">
            <Mail size={8} /> <span>{companyInfo.email}</span>
          </p>
          <p id="comp-addr">
            <MapPin size={8} /> <span>{companyInfo.address}</span>
          </p>
          <p id="comp-tin">
            <BadgeCheck size={8} /> <span>{companyInfo.TIN}</span>
          </p>
        </div>

        <div id="cust-div">
          <h3 id="cust-title">Recipient Information</h3>

          <h4 id="cust-name">
            <span className="cust-name1">Name : </span>
            <span className="cust-name2">{invoiceData.customer}</span>
          </h4>

          <h4 id="cust-addr">
            <span className="cust-name1">Address : </span>
            <span className="cust-name2">{invoiceData.address}</span>
          </h4>
        </div>

        <div className="invoice-details" data-status={invoiceData.status}>
          <h4>Invoice # {invoiceData.invoiceNumber}</h4>
          <p>Date Issued: {invoiceData.date}</p>
          <p>Due Date: {invoiceData.expiryDate}</p>
        </div>

        <h3 id="cust-title">Description of Services Provided</h3>

        <div className="table-wraps">
          <table className="invoice-items">
            <thead>
              <tr>
                <th>Description</th>
                <th className="num">Qty</th>
                <th className="num">Rate</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {insertedItems.map((item, i) => (
                <tr key={i}>
                  <td>{item.description}</td>
                  <td className="num">{item.quantity}</td>
                  <td className="num">
                    ₦{(item.unitPrice || 0).toLocaleString()}
                  </td>
                  <td className="num">
                    ₦{(item.lineTotal || 0).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bottom">
          {invoiceData.notes && (
            <p id="inv-notes" className="notes">
              <strong>Payment Details:</strong>
              <br />
              <div className="notes-body preserve">
                {invoiceData.notes}
                <br />
                {companyInfo.payment_instructions}
              </div>
            </p>
          )}

          <p className="pay-info">
            <strong>Payment Information:</strong>
            <div className="pay-info-body preserve">
              Bank Name: {companyInfo.bank_name} <br />
              Account Name: {companyInfo.account_name}
              <br />
              Account Number: {companyInfo.account_number}
            </div>
          </p>

          <div className="invoice-totals">
            <p>Sub-Total: ₦{taxSummary.subtotal.toLocaleString()}</p>
            <p>
              VAT ({(taxSummary.vatRate || 0) * 100}%): ₦
              {taxSummary.vatAmount.toLocaleString()}
            </p>
            <p>Gross Total: ₦{taxSummary.grossTotal.toLocaleString()}</p>
            <p>
              WHT ({(taxSummary.whtRate || 0) * 100}%): ₦
              {taxSummary.whtAmount.toLocaleString()}
            </p>
            <p className="grand">
              <strong>
                Net Receivable: ₦{taxSummary.netReceivable.toLocaleString()}
              </strong>
            </p>
          </div>
        </div>

        <small id="footer" className="muted-center">
          Thank you for Your Business ...
        </small>
      </aside>

      <button onClick={printInvoice} className="pdf-btn btn-primary">
        <FileDown size={16} /> Print / Save as PDF
      </button>

      {isModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-content glass">
            <h3>Add Item</h3>
            <div className="modal-grid">
              <input
                name="description"
                placeholder="Description"
                value={newItem.description}
                onChange={handleModalInputChange}
              />
              <input
                name="quantity"
                type="number"
                placeholder="Quantity"
                value={newItem.quantity}
                onChange={handleModalInputChange}
              />
              <input
                name="unitPrice"
                type="number"
                placeholder="Unit Price"
                value={newItem.unitPrice}
                onChange={handleModalInputChange}
              />
            </div>
            <div className="modal-actions">
              <button className="btn-primary" onClick={insertItem}>
                <PlusCircle size={16} /> Add Item
              </button>
              <button
                className="btn-ghost"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateInvoice;
